// ──────────────────────────────────────────────
// BrandProvider — Applies org brand color as CSS custom property
// ──────────────────────────────────────────────

import { useEffect } from 'react';
import { useAuthStore } from '@/stores/authStore';

/** Parse hex to HSL for Tailwind compatibility */
function hexToHsl(hex: string): { h: number; s: number; l: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return null;

  const r = parseInt(result[1], 16) / 255;
  const g = parseInt(result[2], 16) / 255;
  const b = parseInt(result[3], 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

/** Generate color scale from a base HSL color */
function generateScale(h: number, s: number) {
  return {
    50: `${h} ${Math.min(s + 10, 100)}% 97%`,
    100: `${h} ${Math.min(s + 5, 100)}% 93%`,
    200: `${h} ${s}% 86%`,
    300: `${h} ${s}% 73%`,
    400: `${h} ${Math.max(s - 5, 0)}% 60%`,
    500: `${h} ${Math.max(s - 8, 0)}% 50%`,
    600: `${h} ${Math.max(s - 5, 0)}% 44%`,
    700: `${h} ${Math.max(s - 5, 0)}% 37%`,
    800: `${h} ${Math.max(s - 8, 0)}% 30%`,
    900: `${h} ${Math.max(s - 10, 0)}% 22%`,
    950: `${h} ${Math.max(s - 12, 0)}% 13%`,
  };
}

const DEFAULT_BRAND_COLOR = '#4f46e5'; // Indigo-600

export function BrandProvider({ children }: { children: React.ReactNode }) {
  const org = useAuthStore((s) => s.org);

  useEffect(() => {
    // Read brand_color from org settings (stored after login)
    const brandColor = (org as Record<string, unknown> | null)?.brandColor as string | undefined;
    const color = brandColor || DEFAULT_BRAND_COLOR;

    const hsl = hexToHsl(color);
    if (!hsl) return;

    const scale = generateScale(hsl.h, hsl.s);
    const root = document.documentElement;

    // Set CSS custom properties for the brand color scale
    Object.entries(scale).forEach(([shade, value]) => {
      root.style.setProperty(`--color-primary-${shade}`, value);
    });

    // Set the main brand color for use in non-tailwind contexts
    root.style.setProperty('--brand-color', color);

    return () => {
      // Clean up — restore defaults
      Object.keys(scale).forEach((shade) => {
        root.style.removeProperty(`--color-primary-${shade}`);
      });
      root.style.removeProperty('--brand-color');
    };
  }, [org]);

  return <>{children}</>;
}
